/**
 * The taught path in the box map: START, the waypoints and END in the order the trip drives them,
 * joined up, each mark labelled (S, 1, 2, ..., E). While the operator is teaching, a fainter line
 * runs from the last mark to the gripper -- and on to END once there is one -- which is where the
 * next waypoint would go, so the path can be seen taking shape before a mark is set.
 *
 * Tubes rather than lines: WebGL draws a line one pixel wide whatever width it is asked for, and
 * in the headset that is a thread you cannot find.
 */
import { type Box3, CanvasTexture, CylinderGeometry, Group, Mesh, MeshBasicMaterial, type Object3D, SphereGeometry, Sprite,
         SpriteMaterial, Vector3 } from "three";

import type { PathState } from "../adamo/types";

const COLOUR = "#c38bff";   // violet: not a height on the map's ramp, and not one of the warning colours
const TUBE_R = 0.0025;      // m
const END_R = 0.007;        // m, the START and END balls
const WAY_R = 0.005;        // m, a waypoint's
const LABEL_H = 0.03;       // m, how tall a label is
const LABEL_LIFT = 0.024;   // m, how far above its mark

const UP = new Vector3(0, 1, 0);
const along = new Vector3();
const tool = new Vector3();

export class TaughtPath {
  /** In the arm's base frame, metres: add it where the map's voxels are. */
  readonly group = new Group();
  private readonly tube = new CylinderGeometry(TUBE_R, TUBE_R, 1, 10, 1, true);
  private readonly ball = new SphereGeometry(1, 16, 12);
  private readonly solid = new MeshBasicMaterial({ color: COLOUR });
  private readonly faint = new MeshBasicMaterial({ color: COLOUR, transparent: true, opacity: 0.45, depthWrite: false });
  private readonly segments: Mesh[] = [];
  private readonly marks: Mesh[] = [];
  private readonly labels: Sprite[] = [];
  private readonly labelMaterials = new Map<string, SpriteMaterial>();
  /** Teaching: the last mark to the gripper, and the gripper on to END. */
  private readonly toTool = new Mesh(this.tube, this.faint);
  private readonly toEnd = new Mesh(this.tube, this.faint);
  private points: Vector3[] = [];
  private endSet = false;
  private key = "";

  constructor() {
    this.toTool.visible = this.toEnd.visible = false;
    this.group.add(this.toTool, this.toEnd);
  }

  get empty(): boolean {
    return this.points.length === 0;
  }

  /** Grow `box` (base frame, metres) to take in the taught marks. */
  expand(box: Box3): Box3 {
    for (const p of this.points) box.expandByPoint(p);
    return box;
  }

  /** What is taught, and -- only while teaching -- the tool point (m, base frame) a mark would be set at. */
  set(path: PathState | null | undefined, teachingAt: readonly number[] | null | undefined): void {
    const pts = path?.points ?? [];
    const key = JSON.stringify([pts, path?.start, path?.end]);
    if (key !== this.key) {
      this.key = key;
      this.rebuild(pts, Boolean(path?.start), Boolean(path?.end));
    }
    this.preview(path?.start ? teachingAt ?? null : null);
  }

  private rebuild(pts: [number, number, number][], startSet: boolean, endSet: boolean): void {
    this.points = pts.map((p) => new Vector3(p[0] / 1000, p[1] / 1000, p[2] / 1000));
    const n = this.points.length;
    this.endSet = endSet && n > 0;
    show(this.segments, Math.max(n - 1, 0), () => new Mesh(this.tube, this.solid), this.group)
      .forEach((m, i) => span(m, this.points[i], this.points[i + 1]));
    show(this.marks, n, () => new Mesh(this.ball, this.solid), this.group).forEach((m, i) => {
      const big = (i === 0 && startSet) || (i === n - 1 && this.endSet);
      m.position.copy(this.points[i]);
      m.scale.setScalar(big ? END_R : WAY_R);
    });
    show(this.labels, n, () => this.newLabel(), this.group).forEach((s, i) => {
      const text = (i === 0 && startSet) ? "S" : (i === n - 1 && this.endSet) ? "E" : String(startSet ? i : i + 1);
      s.material = this.labelMaterial(text);
      s.position.copy(this.points[i]).add(along.set(0, 0, LABEL_LIFT));   // base frame: z is up
    });
  }

  private preview(at: readonly number[] | null): void {
    const n = this.points.length;
    if (!at || n === 0) {
      this.toTool.visible = this.toEnd.visible = false;
      return;
    }
    tool.set(at[0], at[1], at[2]);
    // A new waypoint goes after the last one, so before END when there is one.
    const before = this.endSet ? this.points[n - 2] : this.points[n - 1];
    if (before) span(this.toTool, before, tool);
    else this.toTool.visible = false;
    if (this.endSet) span(this.toEnd, tool, this.points[n - 1]);
    else this.toEnd.visible = false;
  }

  private newLabel(): Sprite {
    const s = new Sprite();
    s.scale.set(LABEL_H, LABEL_H, 1);
    s.renderOrder = 12;   // over the voxels and the gripper: a label you cannot read is no label
    return s;
  }

  private labelMaterial(text: string): SpriteMaterial {
    let m = this.labelMaterials.get(text);
    if (m) return m;
    const c = document.createElement("canvas");
    c.width = c.height = 96;
    const g = c.getContext("2d")!;
    g.fillStyle = "rgba(18, 14, 28, 0.85)";
    g.strokeStyle = COLOUR;
    g.lineWidth = 6;
    g.beginPath();
    g.arc(48, 48, 42, 0, 2 * Math.PI);
    g.fill();
    g.stroke();
    g.fillStyle = "#ffffff";
    g.font = `bold ${text.length > 1 ? 44 : 54}px system-ui, sans-serif`;
    g.textAlign = "center";
    g.textBaseline = "middle";
    g.fillText(text, 48, 51);
    m = new SpriteMaterial({ map: new CanvasTexture(c), depthTest: false, transparent: true });
    this.labelMaterials.set(text, m);
    return m;
  }

  dispose(): void {
    this.tube.dispose();
    this.ball.dispose();
    this.solid.dispose();
    this.faint.dispose();
    for (const m of this.labelMaterials.values()) { m.map?.dispose(); m.dispose(); }
  }
}

/** The first `count` of a pool, made as needed and the rest hidden. */
function show<T extends Object3D>(pool: T[], count: number, make: () => T, parent: Group): T[] {
  while (pool.length < count) {
    const o = make();
    pool.push(o);
    parent.add(o);
  }
  pool.forEach((o, i) => { o.visible = i < count; });
  return pool.slice(0, count);
}

/** A unit tube along y, stretched from one point to the other. */
function span(m: Mesh, from: Vector3, to: Vector3): void {
  along.subVectors(to, from);
  const len = along.length();
  m.visible = len > 0.001;
  if (!m.visible) return;
  m.position.copy(from).addScaledVector(along, 0.5);
  m.quaternion.setFromUnitVectors(UP, along.divideScalar(len));
  m.scale.set(1, len, 1);
}
